import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { FormField, Input, Select } from '../../components/Forms';
import { User } from '../../types';

interface UserFilters {
  search: string;
  role: string;
  isActive: string;
}

interface UserFormData {
  username: string;
  fullName: string;
  password: string;
  role: string;
  phone: string;
  email: string;
}

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'مدير النظام' },
  { value: 'manager', label: 'مدير' },
  { value: 'reception', label: 'استقبال' },
  { value: 'operations', label: 'عمليات' },
  { value: 'accounting', label: 'محاسبة' }
];

const emptyForm: UserFormData = {
  username: '',
  fullName: '',
  password: '',
  role: 'reception',
  phone: '',
  email: ''
};

const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: '',
    isActive: ''
  });

  // Add/Edit modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>(emptyForm);
  const [formError, setFormError] = useState('');

  // Reset password modal state
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState('');

  // Fetch users with pagination and filters
  const { data: usersData, isLoading, error } = useQuery({
    queryKey: ['users', currentPage, filters],
    queryFn: () => apiService.getUsers(currentPage, 10, filters.search)
  });

  // Create / update user mutation
  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; payload: Partial<User> }) =>
      data.id
        ? apiService.updateUser(data.id, data.payload)
        : apiService.createUser(data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeFormModal();
    },
    onError: (err: any) => {
      setFormError(err?.message || 'حدث خطأ أثناء حفظ المستخدم');
    }
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => apiService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  // Toggle active status mutation
  const toggleMutation = useMutation({
    mutationFn: (userId: string) => apiService.toggleUserStatus(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: any) => {
      window.alert(err?.message || 'تعذر تغيير حالة المستخدم');
    }
  });

  // Reset password mutation
  const resetMutation = useMutation({
    mutationFn: (data: { id: string; newPassword: string }) =>
      apiService.resetUserPassword(data.id, data.newPassword),
    onSuccess: () => {
      closeResetModal();
    },
    onError: (err: any) => {
      setResetError(err?.message || 'تعذر إعادة تعيين كلمة المرور');
    }
  });

  const handleFilterChange = (field: keyof UserFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setFormError('');
    setShowFormModal(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      fullName: user.fullName,
      password: '',
      role: user.role,
      phone: user.phone || '',
      email: user.email || ''
    });
    setFormError('');
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingUser(null);
    setFormData(emptyForm);
    setFormError('');
  };

  const handleFormChange = (field: keyof UserFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Build payload — only send fields the backend expects
    const payload: Partial<User> & { password?: string } = {
      username: formData.username.trim(),
      fullName: formData.fullName.trim(),
      role: formData.role as User['role'],
      phone: formData.phone.trim() || undefined,
      email: formData.email.trim() || undefined
    };

    if (editingUser) {
      // On edit, only include password if the admin typed a new one
      if (formData.password) {
        payload.password = formData.password;
      }
      saveMutation.mutate({ id: editingUser._id, payload });
    } else {
      if (!formData.password) {
        setFormError('كلمة المرور مطلوبة');
        return;
      }
      payload.password = formData.password;
      saveMutation.mutate({ payload });
    }
  };

  const handleDelete = async (user: User) => {
    if (window.confirm(`هل أنت متأكد من حذف المستخدم "${user.fullName}"؟`)) {
      try {
        await deleteMutation.mutateAsync(user._id);
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  const handleToggleStatus = (user: User) => {
    const action = user.isActive ? 'إلغاء تفعيل' : 'تفعيل';
    if (window.confirm(`هل تريد ${action} المستخدم "${user.fullName}"؟`)) {
      toggleMutation.mutate(user._id);
    }
  };

  const openResetModal = (user: User) => {
    setResetUser(user);
    setNewPassword('');
    setResetError('');
  };

  const closeResetModal = () => {
    setResetUser(null);
    setNewPassword('');
    setResetError('');
  };

  const handleResetSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    if (newPassword.length < 6) {
      setResetError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    if (resetUser) {
      resetMutation.mutate({ id: resetUser._id, newPassword });
    }
  };

  const getRoleLabel = (role: string) => {
    const roleLabels: { [key: string]: string } = {
      super_admin: 'مدير النظام',
      manager: 'مدير',
      reception: 'استقبال',
      operations: 'عمليات',
      accounting: 'محاسبة',
      vendor: 'بائع'
    };
    return roleLabels[role] || role;
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: { [key: string]: string } = {
      super_admin: 'bg-red-100 text-red-800',
      manager: 'bg-purple-100 text-purple-800',
      reception: 'bg-blue-100 text-blue-800',
      operations: 'bg-green-100 text-green-800',
      accounting: 'bg-yellow-100 text-yellow-800',
      vendor: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const formatLastLogin = (lastLogin?: Date) => {
    if (!lastLogin) return 'لم يسجل دخول من قبل';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'منذ أقل من ساعة';
    if (diffInHours < 24) return `منذ ${diffInHours} ساعة`;
    if (diffInHours < 48) return 'منذ يوم واحد';
    return `منذ ${Math.floor(diffInHours / 24)} أيام`;
  };

  const users = usersData?.data || [];
  const pagination = usersData?.pagination || {
    current: 1,
    pages: 1,
    total: 0,
    limit: 10
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">حدث خطأ في تحميل البيانات</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">المستخدمين</h1>
          <p className="text-gray-600">إدارة مستخدمي النظام والصلاحيات</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <PlusIcon className="w-5 h-5 ml-2" />
          إضافة مستخدم جديد
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FormField label="البحث">
            <div className="relative">
              <Input
                placeholder="البحث بالاسم أو اسم المستخدم..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pr-10"
              />
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </FormField>

          <FormField label="الدور">
            <Select
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              options={[
                { value: '', label: 'جميع الأدوار' },
                ...ROLE_OPTIONS
              ]}
            />
          </FormField>

          <FormField label="الحالة">
            <Select
              value={filters.isActive}
              onChange={(e) => handleFilterChange('isActive', e.target.value)}
              options={[
                { value: '', label: 'جميع الحالات' },
                { value: 'true', label: 'نشط' },
                { value: 'false', label: 'غير نشط' }
              ]}
            />
          </FormField>

          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({ search: '', role: '', isActive: '' });
                setCurrentPage(1);
              }}
              className="btn-secondary w-full"
            >
              مسح الفلاتر
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  المستخدم
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الدور
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  معلومات الاتصال
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  آخر تسجيل دخول
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      <span className="mr-3 text-gray-600">جاري التحميل...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    لا توجد مستخدمين مطابقين للبحث
                  </td>
                </tr>
              ) : (
                users.map((user: User) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <UserIcon className="h-6 w-6 text-primary-600" />
                          </div>
                        </div>
                        <div className="mr-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.fullName}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{user.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        {user.phone && (
                          <div className="text-sm text-gray-900">{user.phone}</div>
                        )}
                        {user.email && (
                          <div className="text-sm text-gray-500">{user.email}</div>
                        )}
                        {!user.phone && !user.email && (
                          <span className="text-gray-400">غير محدد</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatLastLogin(user.lastLogin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="تعديل"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(user)}
                          disabled={toggleMutation.isPending}
                          className={`${user.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'} disabled:opacity-50`}
                          title={user.isActive ? 'إلغاء التفعيل' : 'تفعيل'}
                        >
                          {user.isActive ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={() => openResetModal(user)}
                          className="text-purple-600 hover:text-purple-900"
                          title="إعادة تعيين كلمة المرور"
                        >
                          <KeyIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="text-red-600 hover:text-red-900"
                          title="حذف"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                السابق
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.pages))}
                disabled={currentPage === pagination.pages}
                className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                التالي
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  عرض{' '}
                  <span className="font-medium">{((currentPage - 1) * 10) + 1}</span>
                  {' '}إلى{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 10, pagination.total)}
                  </span>
                  {' '}من{' '}
                  <span className="font-medium">{pagination.total}</span>
                  {' '}نتيجة
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    السابق
                  </button>

                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.pages))}
                    disabled={currentPage === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    التالي
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit User Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingUser ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'}
                </h3>
                <button
                  onClick={closeFormModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {formError && (
                <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-4">
                <FormField label="الاسم الكامل" required>
                  <Input
                    value={formData.fullName}
                    onChange={(e) => handleFormChange('fullName', e.target.value)}
                    placeholder="الاسم الكامل"
                    required
                  />
                </FormField>

                <FormField label="اسم المستخدم" required>
                  <Input
                    value={formData.username}
                    onChange={(e) => handleFormChange('username', e.target.value)}
                    placeholder="أحرف إنجليزية وأرقام فقط"
                    required
                  />
                </FormField>

                <FormField label={editingUser ? 'كلمة المرور (اتركها فارغة للإبقاء عليها)' : 'كلمة المرور'} required={!editingUser}>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    placeholder="6 أحرف على الأقل"
                    autoComplete="new-password"
                  />
                </FormField>

                <FormField label="الدور" required>
                  <Select
                    value={formData.role}
                    onChange={(e) => handleFormChange('role', e.target.value)}
                    options={ROLE_OPTIONS}
                  />
                </FormField>

                <FormField label="رقم الهاتف">
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleFormChange('phone', e.target.value)}
                    placeholder="رقم الهاتف"
                  />
                </FormField>

                <FormField label="البريد الإلكتروني">
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    placeholder="example@domain.com"
                  />
                </FormField>

                <div className="flex items-center justify-end space-x-3 space-x-reverse pt-2">
                  <button
                    type="button"
                    onClick={closeFormModal}
                    className="btn-secondary"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    disabled={saveMutation.isPending}
                    className="btn-primary disabled:opacity-50"
                  >
                    {saveMutation.isPending ? 'جاري الحفظ...' : editingUser ? 'حفظ التعديلات' : 'إضافة المستخدم'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  إعادة تعيين كلمة المرور
                </h3>
                <button
                  onClick={closeResetModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                المستخدم: <span className="font-medium text-gray-900">{resetUser.fullName}</span>
              </p>

              {resetError && (
                <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {resetError}
                </div>
              )}

              <form onSubmit={handleResetSubmit} className="space-y-4">
                <FormField label="كلمة المرور الجديدة" required>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="6 أحرف على الأقل"
                    autoComplete="new-password"
                    required
                  />
                </FormField>

                <div className="flex items-center justify-end space-x-3 space-x-reverse pt-2">
                  <button
                    type="button"
                    onClick={closeResetModal}
                    className="btn-secondary"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    disabled={resetMutation.isPending}
                    className="btn-primary disabled:opacity-50"
                  >
                    {resetMutation.isPending ? 'جاري الحفظ...' : 'إعادة التعيين'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
